// src/components/DepartmentListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom'; // For neo4jService if not already initialized
import { departmentQueries } from '../repository/graphQueries';

const DepartmentListView = () => {
    const { neo4jService } = useOutletContext(); // Get service from Dashboard
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchDepartments = useCallback(async () => {
        if (!neo4jService) {
             setError("Neo4j service is not available.");
             setLoading(false);
             return;
        }
        setLoading(true);
        setError(null);
        try {
            // Ensure Neo4j driver is initialized (Dashboard should handle this, but as a fallback)
            if (!neo4jService.driver) {
                const uri = process.env.REACT_APP_NEO4J_URI || 'neo4j://localhost:7687';
                const user = process.env.REACT_APP_NEO4J_USER || 'neo4j';
                const pass = process.env.REACT_APP_NEO4J_PASSWORD || 'password';
                await neo4jService.initialize(uri, user, pass);
            }
            // ADDED: Defensive check for the query string
            const queryToRun = departmentQueries.listAllDepartments;
            if (typeof queryToRun !== 'string' || !queryToRun.trim()) {
                console.error(
                    "DepartmentListView: departmentQueries.listAllDepartments is not a valid Cypher string.",
                    "Value:", queryToRun,
                    "Type:", typeof queryToRun,
                    "Full departmentQueries object:", departmentQueries
                );
                setError("Internal error: Department query definition is missing or invalid. Check console for details.");
                setDepartments([]);
                setLoading(false);
                return;
            }
            // END ADDED CHECK

            const result = await neo4jService.runQuery(departmentQueries.listAllDepartments);
            setDepartments(result.map(record => ({ name: record.department })));
        } catch (err) {
            console.error("Error fetching department list:", err);
            setError("Failed to load department data. " + err.message);
            setDepartments([]);
        } finally {
            setLoading(false);
        }
    }, [neo4jService]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    if (loading) {
        return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Loading departments...</span></div>;
    }

    if (error) {
        return <div className="p-4 bg-red-100 text-red-700 rounded-md"><strong>Error:</strong> {error} <button onClick={fetchDepartments} className="ml-2 bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm">Retry</button></div>;
    }

    if (departments.length === 0) {
        return <div className="p-4 text-center text-gray-500">No departments found.</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Departments</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {departments.map((dept) => (
                    <div 
                        key={dept.name} 
                        className="bg-white shadow-md rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => navigate(`/internal/${dept.name}/overview`)}
                    >
                        <h3 className="text-xl font-medium text-blue-700 hover:text-blue-800">
                            <Link to={`/internal/${dept.name}/overview`}>{dept.name}</Link>
                        </h3>
                        {/* Placeholder for future department-specific stats on this card */}
                        {/* <p className="text-sm text-gray-500 mt-2">Details placeholder...</p> */}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DepartmentListView;